<template>
    <div class="commission-profile-page">

        <div class="profile-section card mb-3">
            <div class="profile-header">
                <div class="profile-photo-wrap">
                    <img
                        v-if="profile.photo"
                        :src="profile.photo"
                        class="profile-avatar"
                        alt="Profile Photo"
                    />
                    <div v-else class="profile-avatar-placeholder">
                        <i class="fa fa-user"></i>
                    </div>
                </div>
                <div class="profile-info-grid">
                    <div class="profile-info-row">
                        <span class="info-label">Name</span>
                        <span class="info-value">{{ profile.full_name }}</span>
                    </div>
                    <div class="profile-info-row">
                        <span class="info-label">ID</span>
                        <span class="info-value">{{ profile.serial_no }}</span>
                    </div>
                    <div class="profile-info-row">
                        <span class="info-label">Rank</span>
                        <span class="info-value">
                            <span v-if="profile.level_name">{{ profile.level_name }} ({{ profile.equivalent_designation }})</span>
                        </span>
                    </div>
                    <div class="profile-info-row">
                        <span class="info-label">Referrer ID / Name</span>
                        <span class="info-value">
                            {{ profile.parent_name }}
                            ({{ profile.parent_serial_no }})
                        </span>
                    </div>
                    <div class="profile-info-row" v-if="profile.dealer_isc_name">
                        <span class="info-label">{{ profile.dealer_isc_role === 'isc' ? 'ISC' : 'Dealer' }} ID / Name</span>
                        <span class="info-value">
                            {{ profile.dealer_isc_name }}
                            ({{ profile.dealer_isc_serial_no }})
                        </span>
                    </div>
                    <div class="profile-info-row">
                        <span class="info-label">Mobile No</span>
                        <span class="info-value">{{ profile.mobile_no }}</span>
                    </div>
                    <div class="profile-info-row">
                        <span class="info-label">Address</span>
                        <span class="info-value">{{ profile.address }}</span>
                    </div>
                </div>
                <div class="profile-actions">
                    <button @click="showPasswordModal = true" class="btn-change-pass">
                        <i class="fa fa-key"></i> Change Password
                    </button>
                </div>
            </div>
            <Loader v-if="profileLoading" />
        </div>

        <SubmissionSection
            :staff-id="profile.serial_no"
            :default-month="defaultMonth"
        />

        <CommissionStatementSection
            :staff-id="profile.serial_no"
            :default-month="defaultMonth"
            :company-info="companyInfo"
        />

        <ChangePasswordModal
            v-if="showPasswordModal"
            @close="showPasswordModal = false"
        />

    </div>
</template>

<script setup>
import { inject, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent';
import handleSdm from '@/services/modules/sdm/sdm';
import handleCompany from '@/services/modules/company';
import SubmissionSection from './SubmissionSection.vue';
import CommissionStatementSection from './CommissionStatementSection.vue';
import ChangePasswordModal from './ChangePasswordModal.vue';

const route = useRoute();
const showError = inject('showError');
const { fetchCompanyInfo } = handleCompany();
const { fetchSdmCommissionProfile } = handleSdm();

const profile = ref({});
const profileLoading = ref(false);
const showPasswordModal = ref(false);
const companyInfo = ref({});

const getLastMonth = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const defaultMonth = getLastMonth();

const getProfileData = async () => {
    profileLoading.value = true;
    const query = `?company_id=${route.params.companyId}`;
    await fetchSdmCommissionProfile(query).then(res => {
        if (!res.status) {
          showError(res.message);
          return;
        }
        profile.value = res.data;
    })
        .catch(() => {})
        .finally(() => {
          profileLoading.value = false;
        });
};

onMounted(async () => {
    await fetchCompanyInfo(route.params.companyId)
        .then(res => {
          companyInfo.value = res.data;
        })
        .catch(() => {});
    await getProfileData();
});
</script>

<style scoped>
.commission-profile-page {
    padding: 1rem;
}

.profile-section {
    overflow: hidden;
}

.profile-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 1.5rem;
    background: linear-gradient(135deg, #f8f9ff 0%, #eef2ff 100%);
    border-bottom: 1px solid #e0e7ff;
    flex-wrap: wrap;
}

.profile-photo-wrap {
    flex-shrink: 0;
}

.profile-avatar {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    object-fit: cover;
    border: 3px solid #6366f1;
    box-shadow: 0 4px 16px rgba(99,102,241,0.2);
}

.profile-avatar-placeholder {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: linear-gradient(135deg, #6366f1, #818cf8);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2.5rem;
    color: #fff;
    box-shadow: 0 4px 16px rgba(99,102,241,0.2);
}

.profile-info-grid {
    flex: 1;
    display: table;
    border-collapse: collapse;
}

.info-label {
    display: table-cell;
    font-size: 0.85rem;
    font-weight: 600;
    color: #374151;
    padding: 0.3rem 1.2rem 0.3rem 0;
    white-space: nowrap;
    vertical-align: top;
}

.info-label::after {
    content: ':';
}

.info-value {
    display: table-cell;
    font-size: 0.85rem;
    font-weight: 500;
    color: #1e293b;
    padding: 0.3rem 0;
    vertical-align: top;
}

.profile-actions {
    display: flex;
    align-items: flex-start;
    align-self: flex-start;
}

.btn-change-pass {
    background: none;
    border: 1.5px solid #ef4444;
    color: #ef4444;
    border-radius: 8px;
    padding: 0.35rem 0.9rem;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
}

.btn-change-pass:hover {
    background: #ef4444;
    color: #fff;
}

@media (max-width: 767px) {
    .commission-profile-page {
        padding: 0.5rem;
    }

    .profile-header {
        flex-direction: column;
        align-items: center;
        gap: 1rem;
        padding: 1rem;
        text-align: center;
    }

    .profile-info-grid {
        display: block;
        width: 100%;
    }

    .profile-info-row {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        border-bottom: 1px solid #e0e7ff;
        padding-bottom: 0.35rem;
    }

    .info-label,
    .info-value {
        display: inline;
    }

    .profile-actions {
        width: 100%;
        justify-content: center;
    }

    .btn-change-pass {
        width: 100%;
        justify-content: center;
    }
}
</style>
